import '@xyflow/react/dist/style.css';
import { useCallback } from 'react';
import {
    ReactFlow,
    addEdge,
    reconnectEdge,
    useReactFlow,
    Background,
    MiniMap,
    getIncomers,
    getOutgoers,
    getConnectedEdges,
    type Connection,
} from '@xyflow/react';
import { Spin } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { useDragAndDrop } from '../contexts/useDragAndDrop';
import { useNodesEdges } from '../contexts/useNodesEdges';
import { useSelectedNodeId } from '../contexts/useSelectedNodeId';
import DraggablePalette from './DraggablePalette';
import EditNodePanel from './drawers/EditNodeDrawer';
import { isValidConnection } from '../utils/validate-flow';
import QuestionNode from './nodes/QuestionNode';
import SectionNode from './nodes/SectionNode';
import ActionNode from './nodes/ActionNode';
import DecisionNode from './nodes/DecisionNode';
import Warnings from './Warnings';
import {
    DEFAULT_QUESTION_DATA,
    DEFAULT_SECTION_DATA,
    DEFAULT_DECISION_DATA,
} from '../utils/constants';

import type {
    FlowEdge as FlowEdgeType,
    ConnectionData,
    FlowNode,
} from '@app/services/aiFlow.types';
import FlowEdge from './edges/FlowEdge';
import ConditionEdge from './edges/ConditionEdge';

type DragAndDropFlowProps = {
    isLoading: boolean;
};

const DragAndDropFlow = ({ isLoading }: DragAndDropFlowProps) => {
    const { dragItemType, setDragItemType } = useDragAndDrop();
    const { setSelectedNodeId } = useSelectedNodeId();
    const { nodes, setNodes, onNodesChange, edges, setEdges, onEdgesChange } = useNodesEdges();
    const { screenToFlowPosition } = useReactFlow();

    const onReconnect = useCallback(
        (oldEdge: FlowEdgeType, newConnection: Connection) => {
            if (newConnection?.targetHandle?.startsWith('condition-target')) {
                const conditionValue = newConnection?.sourceHandle?.split('-')[1] ?? '';
                if (conditionValue === 'complete') {
                    oldEdge.label = 'Completed';
                } else if (conditionValue.startsWith('value:')) {
                    oldEdge.label = conditionValue.replace('value:', '');
                }
            }
            setEdges((eds) => reconnectEdge(oldEdge, newConnection, eds));
        },
        [setEdges]
    );

    const onConnect = useCallback(
        (params: Connection) => {
            if (!params.sourceHandle || !params.targetHandle) return;
            let edgeType: 'primary' | 'secondary' = 'secondary';
            let label = '';
            if (
                params.sourceHandle.includes('flow-source') &&
                params.targetHandle.includes('flow-target')
            ) {
                edgeType = 'primary';
            } else {
                const connectionValue = params.sourceHandle.split('-')[1];
                if (connectionValue === 'complete') {
                    label = 'Completed';
                } else if (connectionValue.startsWith('value:')) {
                    label = connectionValue.replace('value:', '');
                }
            }
            setEdges((eds) =>
                addEdge(
                    {
                        ...params,
                        type: edgeType,
                        label: label,
                    },
                    eds
                )
            );
        },
        [setEdges]
    );

    const onNodesDelete = useCallback(
        (deleted: FlowNode[]) => {
            let remainingNodes = [...nodes];
            setEdges(
                deleted.reduce((acc, node) => {
                    const flowEdges = acc.filter((edge) =>
                        edge.sourceHandle.includes('flow-source')
                    );
                    const incomer = getIncomers(node, remainingNodes, flowEdges)[0];
                    const outgoer = getOutgoers(node, remainingNodes, flowEdges)[0];
                    const connectedEdges = getConnectedEdges([node], acc);

                    const remainingEdges = acc.filter((edge) => !connectedEdges.includes(edge));

                    const newArr: FlowEdgeType[] = [];
                    if (incomer && outgoer) {
                        newArr.push({
                            id: `xy-edge__${incomer.id}${incomer.type}-flow-source-${outgoer.id}${outgoer.type}-flow-target`,
                            source: incomer.id,
                            sourceHandle: `${incomer.type}-flow-source`,
                            target: outgoer.id,
                            targetHandle: `${outgoer.type}-flow-target`,
                            type: 'primary',
                        });
                    }

                    remainingNodes = remainingNodes.filter((n) => n.id !== node.id);
                    return [...remainingEdges, ...newArr];
                }, edges)
            );
        },
        [nodes, edges, setEdges]
    );

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            if (!dragItemType) return;
            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });
            const id = uuidv4();
            let newNode: FlowNode;
            switch (dragItemType) {
                case 'action':
                    newNode = {
                        id,
                        position,
                        type: 'action',
                        data: DEFAULT_SECTION_DATA,
                    };
                    break;
                case 'decision':
                    newNode = {
                        id,
                        position,
                        type: 'decision',
                        data: DEFAULT_DECISION_DATA,
                    };
                    break;
                case 'section':
                    newNode = {
                        id,
                        position,
                        type: 'section',
                        data: DEFAULT_SECTION_DATA,
                    };
                    break;
                case 'question':
                    newNode = {
                        id,
                        position,
                        type: 'question',
                        data: DEFAULT_QUESTION_DATA,
                    };
                    break;
            }
            setSelectedNodeId(id);
            setNodes((nds) => [...nds, newNode]);
            setDragItemType(null);
        },
        [screenToFlowPosition, dragItemType, setDragItemType, setNodes, setSelectedNodeId]
    );

    const isValidConnectionCallBack = useCallback(
        (connection: Connection) => {
            if (!connection.sourceHandle || !connection.targetHandle) {
                return false;
            }
            return isValidConnection(connection as ConnectionData, nodes, edges);
        },
        [nodes, edges]
    );

    const nodeColor = useCallback((node: FlowNode) => {
        switch (node.type) {
            case 'action':
                return '#89ecc1';
            case 'decision':
                return '#ffb371';
            case 'section':
                return '#bab5f0';
            case 'question':
                return '#a2cdf4';
            default:
                return '#eee';
        }
    }, []);

    const nodeTypes = {
        action: ActionNode,
        decision: DecisionNode,
        section: SectionNode,
        question: QuestionNode,
    };

    const edgeTypes = {
        primary: FlowEdge,
        secondary: ConditionEdge,
    };

    return (
        <section className="drag-and-drop-flow">
            <Spin spinning={isLoading} size="large" />
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodesDelete={onNodesDelete}
                onConnect={onConnect}
                onReconnect={onReconnect}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                isValidConnection={isValidConnectionCallBack}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView>
                <Background />
                <MiniMap
                    nodeColor={nodeColor}
                    nodeStrokeWidth={3}
                    zoomable
                    pannable
                    position="bottom-left"
                />
            </ReactFlow>
            <DraggablePalette />
            <EditNodePanel />
            <Warnings />
        </section>
    );
};

export default DragAndDropFlow;
